import {
  brickLedgeGapOutlines,
  brickLedgeOutlines,
  footingOutlines,
  foundationCurbs,
  foundationOutlines,
  foundationPatioSlabs,
  pierOutlines,
  planProfileUnion,
  sectionOutlinesFast,
  slabOutlines,
  stemWallComponentOutlines,
  stemWallGapOutlines,
  stemWallInsetOutlines,
} from "building-generation/elements/foundation/foundationProfiles";
import { levelIsAbove, levelIsAboveOrEqual, levelIsBelow } from "building-generation/elements/level/levelTools";
import {
  foundationTagLeaderEnd,
  makeFoundationConditionTags,
  makeFoundationSlabLabels,
  makeFoundationUtilityPlanLinework,
} from "building-generation/utils/foundation";
import type { Building } from "building-types/building/buildingTypes";
import type {
  GeneratedFoundationSuperSystem,
  GeneratedFoundationSubSystem,
} from "building-types/foundations/foundationTypes";
import { type FoundationConditionTag } from "building-types/foundations/foundationTypes";
import type { DocEntityHandle, FoundationHandle, LevelHandle } from "building-types/handles/docEntityHandleTypes";
import { SheetGeometryCategory } from "building-types/views/sheetGeometryCategory";
import type { SheetGeometry, SheetPolylineGeometry, SheetTextGeometry } from "building-types/views/sheetGeometryTypes";
import { sheetPolylineGeometryNew, sheetTextGeometryNew } from "building-types/views/sheetGeometryTypes";
import colors from "common/constants/colorsOld";
import { ZERO_POINTS } from "common/constants/scaleFactors";
import { percentNew, sheetPointsNew } from "common/utils/nominalNumbers";
import { isVoidFloorAssemblyOverride } from "doc/elements/floor/floorAssembly";
import { FOUNDATION_TAG_WIDTH, LW_EXTRA_BOLD } from "doc/plans/constants";
import type { PlanGenerationContext } from "doc/plans/planGeometrySource";
import { PlanGeometryBuilder, type Obstacles } from "doc/plans/planGeometrySource";
import type { PlanGeometryGeneratorResult } from "doc/plans/planGeometrySourceTypes";
import { planTextGeometryAABB, textGeometryInBuiltInView } from "doc/plans/primitives/textPrimitive";
import { aabb2ContainsVec2 } from "geom/aabb2";
import { aabb2ByCenterWidthHeight, aabb2Center } from "geom/aabb2";
import { aabb2Height } from "geom/aabb2Types";
import { aabb2Width } from "geom/aabb2Types";
import type { AABB2 } from "geom/aabb2Types";
import { HatchMaterialId } from "geom/hatchMaterialId";
import { hatchProceduralMaterialNew } from "geom/hatchTypes";
import { polyLine2AABB2 } from "geom/polyLine2";
import { type PolyLine2 } from "geom/polyLine2Types";
import { collectPolylinesFromExtrusions, floorExtrusionPolylinesNew } from "lib/house/floorExtrusionPolylines";
import {
  filterSlabPolygonsToReportingUnit,
  footprintInReportingUnitRegions,
  reportingUnitRoomRegionBBs,
} from "lib/house/plans/reportingUnitRooms";
import type { SheetTitle } from "lib/house/plans/sheetInfo";
import { slideFoundationTags } from "lib/house/plans/slideFoundationTags";
import { makeCallOutTagGeometry } from "./planTags";

function forEachGeneratedFoundationSubSystem(
  building: Building,
  levelId: string,
  callBack: (foundationSubSystem: GeneratedFoundationSubSystem, handle?: FoundationHandle) => void,
): void {
  const { foundations, levels } = building;

  for (const foundationId in foundations) {
    const foundationSubSystem = foundations[foundationId];
    const { levelId: foundationLevelId } = foundationSubSystem;

    if (!levelIsAboveOrEqual(foundationLevelId, levelId, levels)) {
      continue;
    }

    const handle: FoundationHandle = { type: "FOUNDATION", id: foundationId };
    callBack(foundationSubSystem, handle);
  }
}

function forEachGeneratedFoundationSuperSystem(
  building: Building,
  levelId: string,
  callBack: (foundationSuperSystem: GeneratedFoundationSuperSystem, handle?: FoundationHandle) => void,
): void {
  const { foundationSuperSystems, levels } = building;

  for (const foundationSuperSystemId in foundationSuperSystems) {
    const foundationSuperSystem = foundationSuperSystems[foundationSuperSystemId];
    const { levelId: foundationSuperSystemLevelId } = foundationSuperSystem;

    if (!levelIsAboveOrEqual(foundationSuperSystemLevelId, levelId, levels)) {
      continue;
    }

    const handle: FoundationHandle = { type: "FOUNDATION", id: foundationSuperSystemId };
    callBack(foundationSuperSystem, handle);
  }
}

/**
 * Iterates over both foundation subsystems and super systems together.
 * This is needed for functions like pierOutlines that require both.
 */
function forEachFoundation(
  building: Building,
  levelId: string,
  callBack: (foundation: {
    foundationSubSystem: GeneratedFoundationSubSystem;
    foundationSuperSystem: GeneratedFoundationSuperSystem;
  }) => void,
): void {
  const { foundations, foundationSuperSystems, levels } = building;

  for (const id in levels) {
    if (!levelIsAboveOrEqual(id, levelId, levels)) {
      continue;
    }
    // Find foundation super-system for this level
    let foundationSuperSystem: GeneratedFoundationSuperSystem | undefined;
    for (const foundationSuperSystemId in foundationSuperSystems) {
      const foundationSuperSystemCandidate = foundationSuperSystems[foundationSuperSystemId];
      if (foundationSuperSystemCandidate.levelId === id) {
        foundationSuperSystem = foundationSuperSystemCandidate;
        break;
      }
    }
    if (!foundationSuperSystem) {
      continue;
    }
    // Find foundation sub-system for this level
    let foundationSubSystem: GeneratedFoundationSubSystem | undefined;
    for (const foundationId in foundations) {
      const foundation = foundations[foundationId];
      if (foundation.levelId === id) {
        foundationSubSystem = foundation;
        break;
      }
    }
    if (!foundationSubSystem) {
      continue;
    }
    callBack({
      foundationSubSystem,
      foundationSuperSystem,
    });
  }
}

/**
 * Bounding boxes of the selected reporting unit's footprint on this level: its kept slab polygons
 * plus its room outlines. Returns undefined when no unit filter is active.
 */
function reportingUnitFoundationRegionBBs(
  building: Building,
  levelId: string,
  selectedReportingUnitLabelId: string | undefined,
): AABB2[] | undefined {
  const regionBBs = reportingUnitRoomRegionBBs(building, selectedReportingUnitLabelId);
  if (!regionBBs) {
    return undefined;
  }

  for (const superSystemId in building.foundationSuperSystems) {
    const superSystem = building.foundationSuperSystems[superSystemId];
    if (levelIsBelow(superSystem.levelId, levelId, building.levels)) {
      continue;
    }
    for (const polygon of filterSlabPolygonsToReportingUnit(
      building,
      selectedReportingUnitLabelId,
      slabOutlines(superSystem),
    )) {
      regionBBs.push(polyLine2AABB2(polygon));
    }
  }

  return regionBBs;
}

/**
 * Builds a predicate keeping only the foundation outlines the selected unit stands on, since one
 * sub-system can span several buildings. Not exclusive: a shared slab is kept on both units'
 * sheets. Keeps every outline when no unit filter is active.
 */
export function makeReportingUnitFoundationOutlineFilter(
  building: Building,
  levelId: string,
  selectedReportingUnitLabelId: string | undefined,
): (outlines: readonly PolyLine2[]) => PolyLine2[] {
  const regionBBs = reportingUnitFoundationRegionBBs(building, levelId, selectedReportingUnitLabelId);
  if (!regionBBs) {
    return (outlines) => [...outlines];
  }

  return (outlines) =>
    outlines.filter((outline) => footprintInReportingUnitRegions(polyLine2AABB2(outline), regionBBs));
}

export function makeSheetGeometryFromPiers({
  building,
  levelId,
  selectedReportingUnitLabelId,
}: PlanGenerationContext): PlanGeometryGeneratorResult {
  const builder = new PlanGeometryBuilder();

  const { levels } = building;

  const pierEdgesSolid: PolyLine2[] = [];
  const pierEdgesDashed: PolyLine2[] = [];
  const outlineFilter = makeReportingUnitFoundationOutlineFilter(building, levelId, selectedReportingUnitLabelId);

  forEachFoundation(building, levelId, (foundation) => {
    const { solid, dashed } = pierOutlines(foundation.foundationSuperSystem, foundation.foundationSubSystem);

    pierEdgesSolid.push(...outlineFilter(solid));
    pierEdgesDashed.push(...outlineFilter(dashed));
  });

  builder.addGeometry(
    sheetPolylineGeometryNew({
      handle: levels[levelId].handle,
      paths: pierEdgesSolid,
    }),
    SheetGeometryCategory.PIER,
  );
  builder.addGeometry(
    sheetPolylineGeometryNew({
      handle: levels[levelId].handle,
      paths: pierEdgesDashed,
    }),
    SheetGeometryCategory.PIER_HIDDEN,
  );

  return builder;
}

export function makeSheetGeometryFromStemWallUnionedEdges({
  building,
  levelId,
  selectedReportingUnitLabelId,
}: PlanGenerationContext): SheetGeometry[] {
  const stemWallOutlines: PolyLine2[] = [];
  const outlineFilter = makeReportingUnitFoundationOutlineFilter(building, levelId, selectedReportingUnitLabelId);

  forEachGeneratedFoundationSubSystem(building, levelId, (foundationSubSystem) => {
    const outlines =
      foundationSubSystem.levelId === levelId
        ? stemWallInsetOutlines(foundationSubSystem, false)
        : stemWallComponentOutlines(foundationSubSystem, false);
    stemWallOutlines.push(...outlineFilter(outlines));
  });

  const handle: LevelHandle = { type: "LEVEL", id: levelId };

  return [
    sheetPolylineGeometryNew({
      handle,
      paths: planProfileUnion(stemWallOutlines),
    }),
  ];
}

/**
 * Derives a grouping key from a doc Foundation entity's assembly and datum references.
 * Subsystems whose doc entities share the same assembly + datums are unioned together
 * so no separation line is drawn between them on the plan.
 */
function docFoundationAssemblyDatumsKey(building: Building, foundationId: string): string {
  const docFoundation = building.doc.foundations[foundationId];
  if (!docFoundation) {
    return foundationId;
  }
  return [
    docFoundation.foundationAssemblyId ?? "",
    docFoundation.topDatumId ?? "",
    docFoundation.bottomDatumId ?? "",
    docFoundation.lateralDatumId ?? "",
    docFoundation.brickledgeTopDatumId ?? "",
  ].join(".");
}

export function makeSheetGeometryFromStemWallAbove({
  building,
  levelId,
  selectedReportingUnitLabelId,
}: PlanGenerationContext): SheetGeometry[] {
  const groupedOutlines = new Map<string, PolyLine2[]>();
  const outlineFilter = makeReportingUnitFoundationOutlineFilter(building, levelId, selectedReportingUnitLabelId);

  forEachGeneratedFoundationSubSystem(building, levelId, (foundationSubSystem, handle) => {
    const outlines = stemWallComponentOutlines(foundationSubSystem, false);
    const filtered = outlineFilter(outlines);
    const foundationId = handle?.id ?? foundationSubSystem.id;
    const key = docFoundationAssemblyDatumsKey(building, foundationId);
    let group = groupedOutlines.get(key);
    if (!group) {
      group = [];
      groupedOutlines.set(key, group);
    }
    group.push(...filtered);
  });

  const levelHandle: LevelHandle = { type: "LEVEL", id: levelId };
  const result: SheetGeometry[] = [];
  for (const paths of groupedOutlines.values()) {
    result.push(
      sheetPolylineGeometryNew({
        handle: levelHandle,
        paths: planProfileUnion(paths),
      }),
    );
  }

  return result;
}

export function makeSheetGeometryFromStemWallGaps({
  building,
  levelId,
  selectedReportingUnitLabelId,
}: PlanGenerationContext): SheetGeometry[] {
  const stemWallGapEdges: PolyLine2[] = [];
  const outlineFilter = makeReportingUnitFoundationOutlineFilter(building, levelId, selectedReportingUnitLabelId);

  forEachGeneratedFoundationSubSystem(building, levelId, (foundationSubSystem) => {
    const outlines = stemWallGapOutlines(foundationSubSystem, undefined, false);
    stemWallGapEdges.push(...outlineFilter(outlines));
  });

  const handle: LevelHandle = { type: "LEVEL", id: levelId };

  return [
    // line masking
    sheetPolylineGeometryNew({
      handle,
      paths: stemWallGapEdges,
      linewidth: LW_EXTRA_BOLD,
      fill: false,
      strokeColor: colors.white,
    }),
    sheetPolylineGeometryNew({
      handle,
      paths: stemWallGapEdges,
      fill: false,
    }),
  ];
}

export function makeSheetGeometryFromStemWallCut({
  building,
  levelId,
  selectedReportingUnitLabelId,
}: PlanGenerationContext): SheetGeometry[] {
  const handle: LevelHandle = { type: "LEVEL", id: levelId };
  const stemWallEdges: PolyLine2[] = [];
  const outlineFilter = makeReportingUnitFoundationOutlineFilter(building, levelId, selectedReportingUnitLabelId);

  forEachGeneratedFoundationSubSystem(building, levelId, (foundationSubSystem) => {
    const outlines = stemWallInsetOutlines(foundationSubSystem);
    stemWallEdges.push(...outlineFilter(outlines));
  });

  return [
    sheetPolylineGeometryNew({
      handle,
      paths: planProfileUnion(stemWallEdges),
    }),
  ];
}

export function makeSheetGeometryFromPatioSlabs({
  building,
  levelId,
  selectedReportingUnitLabelId,
}: PlanGenerationContext) {
  const result: SheetGeometry[] = [];

  const { foundationSuperSystems } = building;
  const outlineFilter = makeReportingUnitFoundationOutlineFilter(building, levelId, selectedReportingUnitLabelId);

  for (const foundationSuperSystemId in foundationSuperSystems) {
    const foundationSuperSystem = foundationSuperSystems[foundationSuperSystemId];
    if (foundationSuperSystem && foundationSuperSystem.levelId === levelId) {
      const foundationHandle: FoundationHandle = { type: "FOUNDATION", id: foundationSuperSystemId };
      const patioOutlines: PolyLine2[] = [];
      for (const slab of foundationPatioSlabs(foundationSuperSystem)) {
        patioOutlines.push(...slab.planProfile);
      }

      result.push(
        sheetPolylineGeometryNew({
          handle: foundationHandle,
          paths: outlineFilter(patioOutlines),
        }),
      );
    }
  }
  return result;
}

export function makeSheetGeometryFromBrickLedgeCut({
  building,
  levelId,
  selectedReportingUnitLabelId,
}: PlanGenerationContext): SheetGeometry[] {
  const result: SheetGeometry[] = [];
  const outlineFilter = makeReportingUnitFoundationOutlineFilter(building, levelId, selectedReportingUnitLabelId);

  forEachGeneratedFoundationSubSystem(building, levelId, (foundation, handle) => {
    const rawBrickLedgeEdges: PolyLine2[] = brickLedgeOutlines(foundation, false);
    const brickLedgeEdges = outlineFilter(rawBrickLedgeEdges);

    // mask
    result.push(
      sheetPolylineGeometryNew({
        handle,
        paths: brickLedgeEdges,
        linewidth: ZERO_POINTS,
        fill: true,
        dash: false,
        fillOpacity: percentNew(1),
        fillColor: colors.white,
        strokeOpacity: percentNew(0),
      }),
    );

    // hatching
    result.push(
      sheetPolylineGeometryNew({
        handle,
        paths: brickLedgeEdges,
        linewidth: ZERO_POINTS,
        fill: false,
        dash: false,
        fillOpacity: percentNew(0),
        fillColor: colors.white,
        hatch: true,
        hatchStroke: false,
        hatchLineWidth: sheetPointsNew(0.25),
        hatchStrokeColor: colors.stoneDark,
        hatchMaterial: hatchProceduralMaterialNew(HatchMaterialId.Diagonal_Lines),
      }),
    );

    // cut edges
    result.push(
      sheetPolylineGeometryNew({
        handle,
        paths: planProfileUnion(brickLedgeEdges),
        fill: false,
        fillOpacity: percentNew(0.075),
      }),
    );
  });

  return result;
}

export function makeSheetGeometryFromBrickLedgeProjected({
  building,
  levelId,
  selectedReportingUnitLabelId,
}: PlanGenerationContext): SheetGeometry[] {
  const result: SheetGeometry[] = [];
  const outlineFilter = makeReportingUnitFoundationOutlineFilter(building, levelId, selectedReportingUnitLabelId);

  forEachGeneratedFoundationSubSystem(building, levelId, (foundation, handle) => {
    const rawBrickLedgeGapEdges: PolyLine2[] = brickLedgeGapOutlines(foundation, undefined, false);
    const brickLedgeGapEdges = outlineFilter(rawBrickLedgeGapEdges);

    result.push(
      sheetPolylineGeometryNew({
        handle,
        paths: brickLedgeGapEdges,
        fill: false,
        fillOpacity: percentNew(0.075),
      }),
    );
  });

  return result;
}

export function makeSheetGeometryFromCurbEdges({
  building,
  levelId,
  selectedReportingUnitLabelId,
}: PlanGenerationContext): SheetGeometry[] {
  const result: SheetGeometry[] = [];
  const outlineFilter = makeReportingUnitFoundationOutlineFilter(building, levelId, selectedReportingUnitLabelId);

  forEachGeneratedFoundationSubSystem(building, levelId, (foundationSubSystem, handle) => {
    const rawCurbEdges = sectionOutlinesFast(foundationCurbs(foundationSubSystem));
    const curbEdges = outlineFilter(rawCurbEdges);
    result.push(
      sheetPolylineGeometryNew({
        handle,
        paths: curbEdges,
      }),
    );
  });

  return result;
}

export function makeSheetGeometryFromFootingEdges({
  building,
  levelId,
  selectedReportingUnitLabelId,
}: PlanGenerationContext): SheetGeometry[] {
  const result: SheetGeometry[] = [];
  const outlineFilter = makeReportingUnitFoundationOutlineFilter(building, levelId, selectedReportingUnitLabelId);

  forEachGeneratedFoundationSubSystem(building, levelId, (foundationSubSystem, handle) => {
    const outlines = footingOutlines(foundationSubSystem);
    const filtered = outlineFilter(outlines);
    result.push(
      sheetPolylineGeometryNew({
        handle,
        paths: filtered,
      }),
    );
  });

  return result;
}

export function slabEdgeObstacles(o: Obstacles, slabEdges: SheetPolylineGeometry) {
  if (o.polylineObstacles) {
    o.polylineObstacles.push(...slabEdges.paths);
  }
}

export function makeSheetGeometryFromSlabEdgesAbove({
  building,
  levelId,
  selectedReportingUnitLabelId,
}: PlanGenerationContext): SheetGeometry[] {
  const { levels } = building;
  const result: SheetGeometry[] = [];

  forEachGeneratedFoundationSuperSystem(building, levelId, (foundationSuperSystem) => {
    if (!levelIsAbove(foundationSuperSystem.levelId, levelId, levels)) {
      return;
    }

    const handle = { type: "FOUNDATION", id: foundationSuperSystem.id } as const;
    result.push(
      sheetPolylineGeometryNew({
        handle,
        paths: filterSlabPolygonsToReportingUnit(
          building,
          selectedReportingUnitLabelId,
          slabOutlines(foundationSuperSystem),
        ),
      }),
    );
  });

  return result;
}

export function makeSheetGeometryFromSlabEdgesBelow({
  building,
  levelId,
  selectedReportingUnitLabelId,
}: PlanGenerationContext): SheetGeometry[] {
  const { levels, slabFloorAssemblies, horizontalAssemblyFloors } = building;
  const result: SheetGeometry[] = [];

  forEachGeneratedFoundationSuperSystem(building, levelId, (foundationSuperSystem) => {
    if (levelIsAbove(foundationSuperSystem.levelId, levelId, levels)) {
      return;
    }

    const handle: FoundationHandle = { type: "FOUNDATION", id: foundationSuperSystem.id };
    result.push(
      sheetPolylineGeometryNew({
        handle,
        paths: filterSlabPolygonsToReportingUnit(
          building,
          selectedReportingUnitLabelId,
          slabOutlines(foundationSuperSystem),
        ),
      }),
    );
  });

  const levelHandle = levels[levelId].handle;
  const outlineFilter = makeReportingUnitFoundationOutlineFilter(building, levelId, selectedReportingUnitLabelId);
  for (const boundaryId in slabFloorAssemblies) {
    const assembly = slabFloorAssemblies[boundaryId];
    if (assembly.levelId !== levelId) {
      continue;
    }
    // Assemblies are keyed by bay/boundary id; the floor entity is under parentFloorId.
    const floorEntity = horizontalAssemblyFloors[assembly.parentFloorId] ?? horizontalAssemblyFloors[boundaryId];
    if (!floorEntity || floorEntity.disabled || isVoidFloorAssemblyOverride(floorEntity)) {
      continue;
    }

    const polylines = floorExtrusionPolylinesNew();
    collectPolylinesFromExtrusions(assembly.slab, polylines);
    const outlines = outlineFilter(polylines.outlines);
    const openings = outlineFilter(polylines.openings);
    if (outlines.length > 0) {
      result.push(sheetPolylineGeometryNew({ handle: levelHandle, paths: outlines }));
    }
    if (openings.length > 0) {
      result.push(sheetPolylineGeometryNew({ handle: levelHandle, paths: openings }));
    }
  }

  return result;
}

interface PlanUtilitySlabEdgesProps {
  building: Building;
  levelId: string;
  selectedReportingUnitLabelId?: string;
}

export function makeSheetGeometryForFoundationOutline({
  building,
  levelId,
  selectedReportingUnitLabelId,
}: PlanUtilitySlabEdgesProps): SheetGeometry[] {
  const { foundationSuperSystems } = building;
  const sgOut: SheetGeometry[] = [];
  const outlineFilter = makeReportingUnitFoundationOutlineFilter(building, levelId, selectedReportingUnitLabelId);

  for (const foundationSuperSystemId in foundationSuperSystems) {
    const foundationSuperSystem = foundationSuperSystems[foundationSuperSystemId];
    if (foundationSuperSystem.levelId !== levelId) {
      continue;
    }

    const foundationHandle: FoundationHandle = { type: "FOUNDATION", id: foundationSuperSystemId };

    const slabEdges: PolyLine2[] = foundationOutlines(foundationSuperSystem);

    sgOut.push(
      sheetPolylineGeometryNew({
        handle: foundationHandle,
        paths: outlineFilter(slabEdges),
      }),
    );
  }

  return sgOut;
}

export async function makeSheetGeometryForUtilityFoundationLinework({
  building,
  levelId,
}: PlanGenerationContext): Promise<SheetGeometry[]> {
  const { linework, dashedLinework } = await makeFoundationUtilityPlanLinework(building, levelId);
  const { levels } = building;

  return [
    sheetPolylineGeometryNew({
      handle: levels[levelId].handle,
      paths: dashedLinework,
      linewidth: ZERO_POINTS,
      fill: false,
      dash: false,
      fillOpacity: percentNew(0.075),
      hatch: true,
      hatchStroke: false,
      hatchLineWidth: sheetPointsNew(0.25),
      hatchStrokeColor: "gray",
      hatchMaterial: hatchProceduralMaterialNew(HatchMaterialId.Diagonal_CrossHatch),
    }),
    sheetPolylineGeometryNew({
      handle: levels[levelId].handle,
      paths: linework,
      fill: false,
    }),
  ];
}

export function makeSheetGeometryForFoundationConditionTags({
  building,
  levelId,
  planRotationRads,
  selectedReportingUnitLabelId,
}: PlanGenerationContext): SheetGeometry[] {
  let conditionTags = makeFoundationConditionTags(building, building.doc.foundationAssemblies, levelId);
  const regionBBs = reportingUnitFoundationRegionBBs(building, levelId, selectedReportingUnitLabelId);
  if (regionBBs) {
    conditionTags = conditionTags.filter((conditionTag) =>
      regionBBs.some((region) => aabb2ContainsVec2(region, conditionTag.x, conditionTag.y)),
    );
  }
  return makeConditionTagSheetGeometries({
    conditionTags,
    handle: building.levels[levelId].handle,
    planRotationRads,
  });
}

interface PlanSlabLabelsProps {
  building: Building;
  levelId: string;
  obstacles?: AABB2[];
  selectedReportingUnitLabelId?: string;
}

export function makeConditionTagSheetGeometries({
  conditionTags,
  handle,
  planRotationRads = 0,
  detailSheetTitle,
  tagObstaclesAABB = [],
  tagObstaclePls = [],
}: {
  conditionTags: FoundationConditionTag[];
  handle: DocEntityHandle;
  planRotationRads?: number;
  detailSheetTitle?: SheetTitle;
  tagObstaclesAABB?: any[];
  tagObstaclePls?: any[];
}): SheetGeometry[] {
  const result: SheetGeometry[] = [];
  const leaders: SheetGeometry[] = [];
  const feet: SheetGeometry[] = [];
  const heads: SheetGeometry[] = [];

  const updatedFoundationConditionTags = slideFoundationTags(conditionTags, tagObstaclesAABB, tagObstaclePls);

  updatedFoundationConditionTags.forEach((conditionTag) => {
    const x = conditionTag.x;
    const y = conditionTag.y;

    const [endX, endY] = foundationTagLeaderEnd(conditionTag);

    const { drawingNumber } = conditionTag.data;

    const tagGeom = makeCallOutTagGeometry({
      topText: conditionTag.label ?? String(drawingNumber),
      bottomText: detailSheetTitle?.number ?? "",
      direction: conditionTag.direction,
      planRotationRads,
      startX: x,
      startY: y,
      endX,
      endY,
      handle,
      /**
       * @NOTE - if you change this value, make sure to change the
       * appropriate relaxation size in slideFoundationTags
       */
      size: FOUNDATION_TAG_WIDTH,
    });

    const { foot, leader, ...head } = tagGeom;
    feet.push(foot);
    leaders.push(leader);
    heads.push(...Object.values(head));
  });

  /**
   * @NOTE - we need to do this ordering specifically
   * because this is the only way to ensure draw order of tag heads
   * above leaders for now
   */
  result.push(...leaders);
  result.push(...feet);
  result.push(...heads);

  return result;
}

export function slabLabelAddObstacles(aabbObstacles: AABB2[], slabLabel: SheetTextGeometry) {
  const aabb = planTextGeometryAABB(textGeometryInBuiltInView(slabLabel));
  const max = Math.max(aabb2Width(aabb), aabb2Height(aabb));
  const [cx, cy] = aabb2Center(aabb);
  aabbObstacles.push(aabb2ByCenterWidthHeight(cx, cy, max, max));
}

export function slabLabelMakePlanEntitiesAndObstacles({
  building,
  levelId,
  obstacles,
}: PlanSlabLabelsProps): SheetGeometry[] {
  const sheetGeometry: SheetGeometry[] = makeSheetGeometryFromSlabLabels({
    building,
    levelId,
  });
  if (obstacles) {
    for (const e of sheetGeometry) {
      slabLabelAddObstacles(obstacles, e as SheetTextGeometry);
    }
  }

  return sheetGeometry;
}

export function makeSheetGeometryFromSlabLabels({
  building,
  levelId,
  selectedReportingUnitLabelId,
}: PlanSlabLabelsProps): SheetGeometry[] {
  const result: SheetGeometry[] = [];
  const {
    settings: { planset: planSettings },
    levels,
  } = building;

  const { includeExcavationNote } = planSettings;

  const regionBBs = reportingUnitFoundationRegionBBs(building, levelId, selectedReportingUnitLabelId);
  const slabLabels = makeFoundationSlabLabels(building, levelId, includeExcavationNote);
  for (const slabLabel of slabLabels) {
    if (regionBBs && !regionBBs.some((region) => aabb2ContainsVec2(region, slabLabel.centerX, slabLabel.centerY))) {
      continue;
    }
    result.push(
      sheetTextGeometryNew({
        handle: levels[levelId].handle,
        text: slabLabel.header,
        x: slabLabel.centerX,
        y: slabLabel.centerY,
        rotationBasis: "SHEET",
      }),
    );
  }
  return result;
}
